/* eslint-disable max-classes-per-file */
import { PropertyValues } from 'lit';

import { Component } from '../../models';
import type { BaseArray } from '../virtualIndexArray';
import type { SpatialNavigationEvent } from '../../components/spatialnavigationprovider/spatialnavigationprovider.events';
import { getElementOrHost } from '../dom';

import type { Constructor } from './index.types';
import { Actions, ACTIONS, KeyToActionInterface, KeyToActionMixin, NAV_MODES } from './KeyToActionMixin';
import { KeyDownHandledMixin, KeyDownHandledMixinInterface } from './KeyDownHandledMixin';
import { LIFE_CYCLE_EVENTS } from './lifecycle/lifecycle.contants';

export declare abstract class ListNavigationMixinInterface {
  protected loop: 'true' | 'false';

  protected initialFocus: number;

  protected orientation: 'vertical' | 'horizontal';

  protected abstract get navItems(): BaseArray<HTMLElement>;

  protected resetTabIndexes(index: number, focusElement?: boolean): void;

  protected resetTabIndexAndSetFocus(
    newIndex: number,
    oldIndex?: number,
    focusNewItem?: boolean,
    scrollToNewItem?: boolean,
  ): boolean;

  protected setInitialFocus(): void;

  protected handleNavigationKeyDown(event: KeyboardEvent): void;
}

/**
 * This mixin extends the passed class with list like navigation capabilities.
 *
 * It handles up & down (or left & right for horizontal orientation) arrow keys, home and end keys to navigate through a list of items.
 * Key mapping aligned to reading direction (RTL or LTR).
 *
 * @example
 * ```ts
 * class MyComponent extends ListNavigationMixin(Component) {
 *   protected override loop = false; // Enable looping navigation
 *   protected override orientation = 'horizontal'; // Set horizontal navigation
 *
 *   protected get navItems() {
 *      return this.shadowRoot?.querySelectorAll('.mdc-listitem') || [];
 *   }
 * }
 * ```
 * @param superClass - The class to extend with the mixin.
 */
export const ListNavigationMixin = <T extends Constructor<Component>>(superClass: T) => {
  abstract class InnerMixinClass extends KeyDownHandledMixin(KeyToActionMixin(superClass)) {
    /**
     * Whether to loop navigation when reaching the end of the list.
     * If 'true', pressing the down arrow on the last item will focus the first item,
     * and pressing the up arrow on the first item will focus the last item.
     * If 'false', navigation will stop at the first or last item.
     *
     * @default 'true'
     * @internal
     */
    protected loop: 'true' | 'false' = 'true';

    /**
     * Whether to propagate all key events to parent components.
     * If true, all key events will bubble up and can be handled by parent components.
     * If false, navigation key events handled by this mixin will not propagate further.
     *
     * @default false
     * @internal
     */
    protected propagateAllKeyEvents = false;

    /**
     * The index of the item to focus initially when the component is first updated.
     *
     * @default 0
     * @internal
     */
    protected initialFocus: number = 0;

    /**
     * The orientation of the list.
     * Controls the direction of keyboard navigation:
     * - 'vertical': Up/Down arrow keys navigate between items
     * - 'horizontal': Left/Right arrow keys navigate between items
     *
     * @default 'vertical'
     * @internal
     */
    protected orientation: 'vertical' | 'horizontal' = 'vertical';

    /**
     * Get list items from the passed property
     * @internal
     */
    protected abstract get navItems(): BaseArray<HTMLElement>;

    constructor(...rest: any[]) {
      super(...rest);

      this.addEventListener(LIFE_CYCLE_EVENTS.CREATED, this.handleItemCreation);
      this.addEventListener('keydown', this.handleNavigationKeyDown.bind(this));
      this.addEventListener('click', this.handleNavigationClick);
    }

    override connectedCallback() {
      super.connectedCallback();
      // Most probably triggered outside this component, so we need to listen on document
      document.addEventListener('navbeforefocus', this.handleNavBeforeFocus);
    }

    override disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener('navbeforefocus', this.handleNavBeforeFocus);
    }

    /**
     * Register created event listener on the item when it is created.
     *
     * Make sure the first created item is focusable. It is useful when the parent element moved.
     *
     * @param event - The event triggered when an item is created.
     * @internal
     */
    protected handleItemCreation = (event: CustomEvent | Event) => {
      const { target } = event;

      const isLifecycleEvent = event instanceof CustomEvent && event.detail?.lifecycle;

      if (this.navItems.length === 0 && target instanceof Component && isLifecycleEvent) {
        target.setAttribute('tabindex', '0');
      }
    };

    /**
     * Reset tabindex and set focus to the first item in the list after the component is first updated.
     *
     * @param changedProperties - The properties that have changed since the last update.
     */
    protected override async firstUpdated(changedProperties: PropertyValues) {
      super.firstUpdated(changedProperties);

      this.setInitialFocus();
    }

    protected setInitialFocus() {
      const indexToFocus = Math.max(Math.min(this.initialFocus, this.navItems.length - 1), 0);
      this.resetTabIndexAndSetFocus(indexToFocus, undefined, false);
    }

    /**
     * Handles keydown events for navigation within the list.
     * It allows users to navigate through the list items using arrow keys, home, and end keys.
     * The navigation is based on the current focused item and the direction of the layout (RTL or LTR).
     *
     * By default, it will stop propagation for key events which are handled by the component.
     * Check the mixin options to change this behavior.
     *
     * @param event - The keyboard event triggered by user interaction.
     * @internal
     */
    protected handleNavigationKeyDown(event: KeyboardEvent) {
      const action = this.getActionForKeyEvent(event, true);
      const isVertical = this.orientation === 'vertical';
      const actionsToHandle = new Set<Actions>(
        isVertical
          ? [ACTIONS.DOWN, ACTIONS.UP, ACTIONS.HOME, ACTIONS.END]
          : [ACTIONS.LEFT, ACTIONS.RIGHT, ACTIONS.HOME, ACTIONS.END],
      );

      if (!action || !actionsToHandle.has(action)) {
        return;
      }

      const target = event.target as HTMLElement;
      const currentIndex = this.getCurrentIndex(target);
      if (currentIndex === -1) return;
      this.resetTabIndexes(currentIndex);
      let navigationHandled = false;

      switch (action) {
        case ACTIONS.HOME: {
          // Move focus to the first item
          navigationHandled = this.resetTabIndexAndSetFocus(0, currentIndex);
          break;
        }
        case ACTIONS.END: {
          // Move focus to the last item
          navigationHandled = this.resetTabIndexAndSetFocus(this.navItems.length - 1, currentIndex);
          break;
        }
        case ACTIONS.DOWN:
        case ACTIONS.RIGHT: {
          // Move focus to the next item
          const eolIndex = this.shouldLoop() ? 0 : currentIndex;
          const newIndex = currentIndex + 1 === this.navItems.length ? eolIndex : currentIndex + 1;
          navigationHandled = this.resetTabIndexAndSetFocus(newIndex, currentIndex);
          break;
        }
        case ACTIONS.UP:
        case ACTIONS.LEFT: {
          // Move focus to the prev item
          const eolIndex = this.shouldLoop() ? this.navItems.length - 1 : currentIndex;
          const newIndex = currentIndex - 1 === -1 ? eolIndex : currentIndex - 1;
          navigationHandled = this.resetTabIndexAndSetFocus(newIndex, currentIndex);
          break;
        }
        default:
          break;
      }

      if (navigationHandled) {
        this.keyDownEventHandled();
        if (!this.propagateAllKeyEvents) {
          event.stopPropagation();
          event.preventDefault();
        }
      }
    }

    /**
     * Handles click events on the navigation items.
     * It retrieves the index of the clicked item and resets the tabindex accordingly.
     *
     * @param event - The mouse event triggered by user interaction.
     * @internal
     */
    protected handleNavigationClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const newIndex = this.getCurrentIndex(target);

      if (newIndex !== -1) {
        // When user clicked on a focusable element inside the item, we update the navigation index, but
        // keep the focus on the clicked element.

        const isActiveElementTarget = document.activeElement === target;
        const isActiveElementContainedByTarget =
          document.activeElement &&
          // eslint-disable-next-line no-bitwise
          document.activeElement.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_CONTAINED_BY;

        const focusNewItem = !(
          this.navItems.at(newIndex) !== target &&
          (isActiveElementTarget || isActiveElementContainedByTarget)
        );
        this.resetTabIndexAndSetFocus(newIndex, undefined, focusNewItem, false);
      }
    };

    /**
     * Retrieves the current index of the item that triggered the event.
     *
     * @param target - The target element that triggered the event.
     * @returns - The index of the current item in the `navItems` array.
     */
    private getCurrentIndex(target: HTMLElement | null | undefined): number {
      if (!target) return -1;

      return this.navItems.findIndex(
        node =>
          node === target ||
          // eslint-disable-next-line no-bitwise
          !!(node.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_CONTAINED_BY),
      );
    }

    /**
     * Reset all tabindex to -1 and set the tabindex of the current item to 0
     *
     * @param index - The index of the currently focused item.
     * @param focusElement - Call focus() on the current item or not.
     */
    protected resetTabIndexes(index: number, focusElement = true) {
      if (this.navItems.length > 0) {
        this.navItems.forEach(item => item.setAttribute('tabindex', '-1'));
        const currentIndex = this.navItems.at(index) ? index : 0;
        const currentItem = this.navItems.at(currentIndex) ?? this.navItems.find(Boolean)!;

        if (currentItem) {
          currentItem.setAttribute('tabindex', '0');
          if (focusElement) {
            currentItem.focus();
          }
        }
      }
    }

    /**
     * Resets the tabindex of the currently focused item and sets focus to a new item.
     *
     * @param newIndex - The index of the new item to focus.
     * @param oldIndex - The index of the currently focused item.
     * @param focusNewItem - Call focus() on the new item or not. It should be false during firstUpdate
     * @param scrollToNewItem - Scroll the new item into view when focused.
     * @returns - true when navigation was successful, false otherwise.
     */
    protected resetTabIndexAndSetFocus(
      newIndex: number,
      oldIndex?: number,
      focusNewItem = true,
      scrollToNewItem = true,
    ): boolean {
      const { navItems } = this;

      if (navItems.length === 0) return false;

      // Ensure newIndex is valid
      const newItem = navItems.at(newIndex) ?? navItems.find(Boolean)!;

      // When there is only one item in the list, we should not reset its tabindex to -1, otherwise it will not be focusable anymore.
      if (newIndex === 0 && oldIndex === 0 && navItems.length === 1) {
        // No need for navigation handling
        return false;
      }

      if (newIndex === oldIndex && newItem && newItem.getAttribute('tabindex') === '0') {
        return false;
      }

      if (oldIndex === undefined) {
        navItems.forEach(item => item.setAttribute('tabindex', '-1'));
      } else if (navItems.at(oldIndex)) {
        // Reset tabindex of the old item
        navItems.at(oldIndex)!.setAttribute('tabindex', '-1');
      }

      newItem.setAttribute('tabindex', '0');

      if (focusNewItem) {
        newItem.focus({ preventScroll: true });

        if (scrollToNewItem) {
          newItem.scrollIntoView({ block: 'nearest' });
        }
      }
      return true;
    }

    private shouldLoop() {
      return this.getKeyboardNavMode() === NAV_MODES.SPATIAL ? false : this.loop !== 'false';
    }

    /**
     * Handles the 'navbeforefocus' event to manage focus transitions into the list.
     * If the focus is moving from outside the list to an element within the list,
     * it prevents the default focus behavior and sets the appropriate tabindex
     * on the target list item.
     *
     * @param event - The SpatialNavigationEvent triggered before focus changes.
     * @internal
     */
    private handleNavBeforeFocus = (event: SpatialNavigationEvent) => {
      const focusCandidate = getElementOrHost(event.relatedTarget as Element);
      if (
        this !== focusCandidate &&
        !this.contains(document.activeElement as HTMLElement) &&
        this.contains(focusCandidate)
      ) {
        event.preventDefault();

        const targetListItem = this.navItems.find(item => item.contains(focusCandidate));
        const currentIndex = this.getCurrentIndex(targetListItem);
        if (currentIndex === -1) return;
        this.resetTabIndexes(currentIndex);
      }
    };
  }

  // Cast return type to your mixin's interface intersected with the superClass type
  return InnerMixinClass as unknown as Constructor<
    Component & ListNavigationMixinInterface & KeyToActionInterface & KeyDownHandledMixinInterface
  > &
    T;
};
